import { merge } from 'es-toolkit/compat';
import type { Component } from 'vue';
import { computed, markRaw, reactive, readonly, ref } from 'vue';

// Utility type that maps each property of T to unknown, making it compatible with Record<string, unknown>
// this ensures that the attributes property of the Modal interface can be used with custom types defined downstream
// while still being compatible with the Record<string, unknown> type
type ToRecord<T> = {
  [P in keyof T]: T[P];
};

// #region modal-interface
export interface Modal<TAttributes = Record<string, unknown>> {
  /**
   * Import the modal that you want to use in your component,
   * then pass that to the composable
   */
  component: Component;

  /**
   * Optional identifier used to close a modal. The default value is `component.name` (if defined).
   */
  name?: string;

  /**
   * Props and listeners to passed through.
   *
   * Listeners need to be formatted as `onEventName`
   */
  attributes?: ToRecord<TAttributes>;

  /**
   * Dyanmic slots that you want rendered in the format of
   * { default: 'my slot content' }
   *
   * The Modals component which renders this list passes the value for slots through
   * a html sanitizer to avoid XSS attacks
   */
  slots?: Record<string, string>;

  /**
   * Custom options to use for the given modal.
   *
   * If options are not passed in, then useModals will set the following values as defaults
   *
   * {
   *  disableDefaultListeners: false
   * }
   */
  options?: {
    disableDefaultListeners?: boolean;
  };
}
// #endregion modal-interface

interface CloseArgs {
  index?: number;
  name?: string;
}

const active = ref<Modal[]>([]);

interface UseModals {
  active: Readonly<Modal[]>;
  current: Readonly<Modal | null>;
  open: <T>(modal: Modal<T>) => void;
  close: (args?: CloseArgs) => void;
  closeAll: () => void;
}

export default function useModals(): UseModals {
  // T is the custom interface of the attributes that are passed to the modal
  function open<T>(modal: Modal<T>) {
    if (!modal.name && modal.component.name) {
      modal.name = modal.component.name;
    }

    //Prevent vue from making the entire component passed in reactive to avoid unnecessary performance overhead
    modal.component = markRaw(modal.component);

    const optionsDefaults: Modal['options'] = {
      disableDefaultListeners: false,
    };

    modal.options = merge({}, optionsDefaults, modal.options);

    active.value.push(modal);
  }

  function close({ index, name }: CloseArgs = {}) {
    if (index === undefined && !name) {
      active.value.shift();
      return;
    }

    if (index !== undefined) {
      active.value.splice(index, 1);
      return;
    }

    if (name) {
      const index = active.value.findIndex((modal) => modal.name === name);

      if (index === -1) return;

      active.value.splice(index, 1);
      return;
    }
  }

  function closeAll() {
    active.value = [];
  }

  const currentModal = computed(() => (active.value.length ? active.value[0] : null));

  // @ts-expect-error - Stupid TS version issues. Todo: remove comment, fix ts issues.
  return reactive({
    /**
     * The list of modals that are currently open.
     */
    active: readonly(active),

    /**
     * The first modal in the active list (index: 0).
     */
    current: readonly(currentModal),

    /**
     * Opens the given modal.
     */
    open,

    /**
     * Closes a modal.
     * If no arguments are passed, then the top-most modal is closed.
     * If an index is passed, then the modal at that index is closed.
     * If a name is passed, then the modal with that name is closed.
     */
    close,

    /**
     * Closes all active/open modals.
     */
    closeAll,
  });
}
